import { useState, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif";

const LOGO_BLACK = "https://customer-assets.emergentagent.com/job_c41ac942-b289-4e5b-a0c4-9845a8b228fb/artifacts/hsq2iw0w_Asset%2010%404x-8.png";

const QUESTIONS = [
  {
    key: "q1",
    question: "How does dating feel for you right now?",
    micro: "Be honest. This is anonymous.",
    options: ["Great", "Pretty good", "Just okay", "Frustrating", "Exhausting"],
  },
  {
    key: "q2",
    question: "What frustrates you most about dating apps?",
    micro: "Pick the one that hits hardest.",
    options: ["Low-quality matches", "Too much time wasted", "Ghosting", "People aren't serious", "Too many options"],
  },
  {
    key: "q3",
    question: "How often are you genuinely excited about someone you meet?",
    micro: "Be honest. This is anonymous.",
    options: ["Often", "Sometimes", "Rarely", "Almost never"],
  },
  {
    key: "q4",
    question: "Would you rather have fewer, better matches than endless browsing?",
    micro: "Be honest. This is anonymous.",
    options: ["Yes", "No", "Not sure"],
  },
  {
    key: "q5",
    question: "Would you try a dating experience with no swiping?",
    micro: "Be honest. This is anonymous.",
    options: ["Yes", "No", "Maybe"],
  },
];

function trackEvent(eventName) {
  if (window.dataLayer) {
    window.dataLayer.push({ event: eventName });
  }
}

export default function PulsePage() {
  const [step, setStep] = useState("intro");
  const [pulseId, setPulseId] = useState(null);
  const [email, setEmail] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [selected, setSelected] = useState(null);

  const questionIndex = typeof step === "number" ? step : -1;

  const transition = useCallback((next) => {
    setTransitioning(true);
    setTimeout(() => {
      setStep(next);
      setSelected(null);
      setTransitioning(false);
    }, 250);
  }, []);

  const handleStart = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pulse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ market: "Austin" }),
      });
      const data = await res.json();
      setPulseId(data.id);
      trackEvent("pulse_start");
      transition(0);
    } catch (e) {
      console.error("Failed to create pulse response", e);
    }
  };

  const handleAnswer = async (questionKey, answer) => {
    setSelected(answer);
    trackEvent(`pulse_${questionKey}_answered`);

    if (pulseId) {
      fetch(`${API_URL}/api/pulse/${pulseId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: questionKey, answer }),
      }).catch(() => {});
    }

    setTimeout(() => {
      if (questionIndex < QUESTIONS.length - 1) {
        transition(questionIndex + 1);
      } else {
        if (pulseId) {
          fetch(`${API_URL}/api/pulse/${pulseId}/complete`, { method: "POST" }).catch(() => {});
        }
        trackEvent("pulse_completed");
        transition("email");
      }
    }, 150);
  };

  const handleEmailSubmit = async () => {
    if (email.trim() && pulseId) {
      await fetch(`${API_URL}/api/pulse/${pulseId}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      }).catch(() => {});
      trackEvent("pulse_email_submitted");
    }
    transition("done");
  };

  const handleSkip = () => transition("done");

  const handleCtaClick = () => trackEvent("pulse_cta_clicked");

  const showProgress = typeof step === "number";
  const progressPercent = showProgress ? ((step + 1) / QUESTIONS.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: FONT }} data-testid="pulse-page">

      {/* ── HEADER ── */}
      <div className="px-5 pt-6 pb-2" data-testid="pulse-header">
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img src={LOGO_BLACK} alt="HAEVN" style={{ height: 22 }} />
          </div>
          {/* Top label */}
          <p className="text-center text-[#999] text-[11px] uppercase tracking-[0.15em] mb-3" style={{ fontWeight: 500 }}>
            Austin Dating Pulse Check
          </p>
          {/* Progress area */}
          {showProgress && (
            <div data-testid="pulse-progress">
              <div className="flex justify-between items-center mb-2">
                <span className="text-[#BBB] text-[11px]" style={{ fontWeight: 500 }}>Austin Pulse</span>
                <span className="text-[#BBB] text-[11px]" style={{ fontWeight: 500 }}>&bull; {step + 1} of {QUESTIONS.length}</span>
              </div>
              <div className="h-[3px] bg-[#F0F0F0] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#D89B00] rounded-full"
                  style={{ width: `${progressPercent}%`, transition: "width 0.4s ease-out" }}
                />
              </div>
            </div>
          )}
          {/* Thin line for non-question screens */}
          {!showProgress && step !== "intro" && (
            <div className="h-[3px] bg-[#D89B00] rounded-full" />
          )}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="flex-1 flex items-center justify-center px-5 py-6">
        <div
          className={`w-full max-w-md transition-all duration-200 ease-out ${
            transitioning ? "opacity-0 translate-y-3" : "opacity-100 translate-y-0"
          }`}
        >

          {/* INTRO */}
          {step === "intro" && (
            <div className="text-center" data-testid="pulse-intro">
              <h1 className="text-[#111] text-[26px] sm:text-[30px] leading-[1.2] mb-4" style={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
                Are Austinites done<br />with swiping?
              </h1>
              <p className="text-[#888] text-[15px] leading-relaxed mb-10" style={{ fontWeight: 400 }}>
                A quick, anonymous check on how dating<br className="sm:hidden" /> actually feels in Austin right now.
              </p>
              <button
                onClick={handleStart}
                className="w-full text-white text-[15px]"
                style={{
                  height: 52,
                  borderRadius: 10,
                  backgroundColor: "#D89B00",
                  fontWeight: 600,
                  border: "1px solid #B88400",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#B88400")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#D89B00")}
                data-testid="pulse-start-btn"
              >
                Start (takes 60 seconds)
              </button>
            </div>
          )}

          {/* QUESTIONS */}
          {typeof step === "number" && QUESTIONS[step] && (
            <div data-testid={`pulse-${QUESTIONS[step].key}`}>
              <h2 className="text-[#111] text-[20px] sm:text-[22px] leading-[1.35] mb-2 text-center" style={{ fontWeight: 600 }}>
                {QUESTIONS[step].question}
              </h2>
              <p className="text-[#BBB] text-[13px] text-center mb-7" style={{ fontWeight: 400 }}>
                {QUESTIONS[step].micro}
              </p>
              <div className={`${QUESTIONS[step].options.length <= 3 ? 'space-y-4' : 'space-y-3.5'}`}>
                {QUESTIONS[step].options.map((option) => {
                  const isSelected = selected === option;
                  const isYes = option === "Yes";
                  const isShortList = QUESTIONS[step].options.length <= 3;
                  return (
                    <button
                      key={option}
                      onClick={() => handleAnswer(QUESTIONS[step].key, option)}
                      className="w-full text-center text-[15px] transition-all duration-100 active:scale-[1.01]"
                      style={{
                        padding: isShortList ? "18px 20px" : "16px 20px",
                        borderRadius: 10,
                        border: isSelected ? "1.5px solid #D89B00" : isYes ? "1.5px solid #D4D4D4" : "1px solid #EEEDEB",
                        backgroundColor: isSelected ? "#FFF9ED" : "#FFFFFF",
                        color: isSelected ? "#111" : isYes ? "#111" : "#333",
                        fontWeight: isSelected ? 600 : isYes ? 500 : 400,
                        cursor: "pointer",
                      }}
                      data-testid={`pulse-option-${option.toLowerCase().replace(/[\s/]+/g, "-")}`}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* EMAIL CAPTURE */}
          {step === "email" && (
            <div className="text-center" data-testid="pulse-email-step">
              <h2 className="text-[#111] text-[20px] sm:text-[22px] leading-[1.3] mb-3" style={{ fontWeight: 600 }}>
                Want to see what we're building in Austin?
              </h2>
              <p className="text-[#888] text-[15px] leading-relaxed mb-1" style={{ fontWeight: 400 }}>
                We're launching a different kind of dating network here.
              </p>
              <p className="text-[#888] text-[15px] leading-relaxed mb-8" style={{ fontWeight: 400 }}>
                No swiping. Just aligned matches.
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional)"
                className="w-full text-[15px] text-[#222] bg-[#FAFAF9] outline-none focus:ring-2 focus:ring-[#D89B00]/20 focus:border-[#D89B00]"
                style={{
                  padding: "14px 16px",
                  borderRadius: 10,
                  border: "1px solid #EEEDEB",
                  marginBottom: 12,
                  fontFamily: FONT,
                }}
                data-testid="pulse-email-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleSkip}
                  className="flex-1 text-[#999] text-[15px] bg-transparent hover:bg-[#F5F5F5] transition-colors"
                  style={{
                    height: 48,
                    borderRadius: 10,
                    border: "1px solid #EEEDEB",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  data-testid="pulse-skip-btn"
                >
                  Skip
                </button>
                <button
                  onClick={handleEmailSubmit}
                  className="flex-1 text-white text-[15px]"
                  style={{
                    height: 48,
                    borderRadius: 10,
                    backgroundColor: "#D89B00",
                    fontWeight: 600,
                    border: "1px solid #B88400",
                    cursor: "pointer",
                  }}
                  data-testid="pulse-see-haevn-btn"
                >
                  See HAEVN
                </button>
              </div>
            </div>
          )}

          {/* COMPLETION */}
          {step === "done" && (
            <div className="text-center" data-testid="pulse-done">
              <h2 className="text-[#111] text-[20px] sm:text-[22px] leading-[1.3] mb-3" style={{ fontWeight: 600 }}>
                Thanks. This helps.
              </h2>
              <p className="text-[#888] text-[15px] leading-relaxed mb-10" style={{ fontWeight: 400 }}>
                We're collecting real input from Austin as we build something different.
              </p>
              <a
                href="/"
                onClick={handleCtaClick}
                className="inline-block text-white text-[15px] w-full"
                style={{
                  height: 52,
                  lineHeight: "52px",
                  borderRadius: 10,
                  backgroundColor: "#D89B00",
                  fontWeight: 600,
                  border: "1px solid #B88400",
                  textDecoration: "none",
                  textAlign: "center",
                }}
                data-testid="pulse-explore-btn"
              >
                Explore HAEVN
              </a>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
